/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : DeliveryMarket.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary  | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, DialogContentText, IconButton, Stack } from "@mui/material";
import deleteIcon from "../../assets/delete_icon.png";

import CustomSearchbarWithApiV2 from "../../components/customComponentsV2/common/searchBar/CustomSearchbarWithApiV2";
import AddButtonV2 from "../../components/customComponentsV2/common/button/AddButtonV2";
import CustomDataGridV2 from "../../components/customComponentsV2/common/datagrid/CustomDataGridV2";

import { HandleApiActions } from "../../redux/actions/actionHandler";
import adminSettingsAPI from "../../redux/actions/adminSetttingAPI";
import { convertUtcToTimeZone } from "../../utils/convertUtcToTimeZone";
import moment from "moment";
import CustomDialogBoxV2 from "../../components/customComponentsV2/common/customDialogBox/CustomDialogBoxV2";
import CustomFormTextFieldV2 from "../../components/customComponentsV2/common/textField/CustomFormTextFieldV2";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import CustomButtonV2 from "../../components/customComponentsV2/common/button/CustomButtonV2";
import PropTypes from "prop-types";

const getDeliveryMarketsFromResponse = (resp) =>
  resp?.data?.product_delivery_markets ??
  resp?.data ??
  resp?.response?.data ??
  [];

const getTotalCountFromResponse = (resp, fallbackData) =>
  resp?.total_count ??
  resp?.totalItems ??
  resp?.total ??
  (Array.isArray(fallbackData) ? fallbackData.length : 0);

const mapDeliveryMarkets = ({
  deliveryMarkets,
  page,
  rowsPerPage,
  region,
  includeTimestamps,
}) =>
  deliveryMarkets?.map((item, index) => {
    const baseData = {
      sno: (page - 1) * rowsPerPage + index + 1,
      id: item?.id || index,
      name: typeof item === "string" ? item : item?.name,
      allData: { name: typeof item === "string" ? item : item?.name },
    };

    if (
      includeTimestamps &&
      typeof item === "object" &&
      (item?.created_at || item?.updated_at)
    ) {
      baseData.created_at = convertUtcToTimeZone(item?.created_at, region);
      baseData.updated_at = convertUtcToTimeZone(item?.updated_at, region);
    }

    return baseData;
  });

function DeliveryMarket({ onExportDataReady, handleExportToExcel }) {
  const adminSettingsReducer = useSelector(
    (state) => state.adminSettingsReducer,
  );
  const timeZoneDetails = useSelector((state) => state.profileReducer);
  const dispatch = useDispatch();

  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(25);
  const [tableRowsData, setTableRowsData] = useState([]);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [totalDeliveryMarketCount, setTotalDeliveryMarketCount] = useState(0);
  const searchQueryRef = useRef("");

  useEffect(() => {
    let isActive = true;
    (async () => {
      const resp = await dispatch(
        HandleApiActions({
          ...adminSettingsAPI.get_dellivery_market_details,
          show_toast: false,
          params: {
            page_number: page,
            page_size: rowsPerPage,
          },
        }),
      );

      if (!isActive) return;

      const deliveryMarkets = getDeliveryMarketsFromResponse(resp);
      const mappedData = mapDeliveryMarkets({
        deliveryMarkets,
        page,
        rowsPerPage,
        region: timeZoneDetails?.profile?.profile?.region,
        includeTimestamps: true,
      });

      setTableRowsData(mappedData || []);
      setTotalDeliveryMarketCount(
        getTotalCountFromResponse(resp, deliveryMarkets),
      );
    })();

    return () => {
      isActive = false;
    };
  }, [dispatch, page, rowsPerPage, timeZoneDetails?.profile?.profile?.region]);

  const handleDeleteClick = useCallback((params) => {
    setSelectedRow(params.row);
    setOpenDeleteDialog(true);
  }, []);

  const handleSearchResults = (data, resp) => {
    if (data !== undefined && data !== null) {
      const mapped = mapDeliveryMarkets({
        deliveryMarkets: data || [],
        page,
        rowsPerPage,
        region: timeZoneDetails?.profile?.profile?.region,
        includeTimestamps: true,
      });
      setTableRowsData(mapped);
      setTotalDeliveryMarketCount(getTotalCountFromResponse(resp, data));
    }
  };

  const COLUMNS = useMemo(() => {
    const deliveryMarkets =
      adminSettingsReducer?.adminSettings?.data?.product_delivery_markets || [];
    const firstItem = deliveryMarkets?.[0];
    const hasTimestamps =
      firstItem &&
      typeof firstItem === "object" &&
      (firstItem.created_at || firstItem.updated_at);

    const baseColumns = [
      {
        field: "sno",
        headerName: "S.No",
        flex: 0.3,
        minWidth: 60,
      },
      {
        field: "name",
        headerName: "Delivery Market Name",
        flex: 1.5,
        minWidth: 250,
      },
    ];

    const timestampColumns = hasTimestamps
      ? [
          {
            field: "created_at",
            headerName: "Created",
            flex: 0.8,
            minWidth: 120,
          },
          {
            field: "updated_at",
            headerName: "Updated",
            flex: 0.8,
            minWidth: 120,
          },
        ]
      : [];

    const actionColumn = {
      field: "actions",
      headerName: "Actions",
      flex: 0.4,
      minWidth: 90,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <IconButton
            size="small"
            sx={{ p: 0 }}
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteClick(params);
            }}
          >
            <img
              src={deleteIcon}
              alt="delete"
              style={{ width: 24, height: 24 }}
            />
          </IconButton>
        </Box>
      ),
    };

    return [...baseColumns, ...timestampColumns, actionColumn];
  }, [
    handleDeleteClick,
    adminSettingsReducer?.adminSettings?.data?.product_delivery_markets,
  ]);

  const filteredRows = useMemo(() => tableRowsData, [tableRowsData]);

  const handlePageChange = useCallback((newPage) => {
    setPage(newPage);
  }, []);

  useEffect(() => {
    if (onExportDataReady && filteredRows) {
      const exportData = filteredRows.map((row) => ({
        "S.No": row.sno,
        "Delivery Market Name": row.name,
        ...(row.created_at && { Created: row.created_at }),
        ...(row.updated_at && { Updated: row.updated_at }),
      }));
      onExportDataReady(exportData);
    }
  }, [filteredRows, onExportDataReady]);

  const handleAddClick = () => {
    setOpenAddDialog(true);
  };

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: {
            xs: "0 0px 2px 0px",
            md: "0 0px 4px 0px",
          },
          flexShrink: 0,
          mt: -2.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box>
            <CustomSearchbarWithApiV2
              params={{
                page_number: page,
                page_size: rowsPerPage,
              }}
              apiAction={adminSettingsAPI.get_dellivery_market_details}
              searchKey={"name"}
              onSearchQueryChange={(query) => {
                if (query !== searchQueryRef.current) {
                  searchQueryRef.current = query;
                  setPage(1);
                }
              }}
              getDataOnSearch={(data, resp) => {
                console.log("Search Data:", resp);
                setTotalDeliveryMarketCount(resp?.total_count || 0);
                handleSearchResults(data, resp);
              }}
              placeholder={"Search"}
            />
          </Box>
          <Box>
            <AddButtonV2 handleOnClick={handleAddClick} />
          </Box>
        </Box>
        <Box>
          <CustomButtonV2
            startIcon={<FileDownloadOutlinedIcon />}
            type="submit"
            size="small"
            btnName="Export"
            handleOnClick={handleExportToExcel}
          />
        </Box>
      </Box>

      <Box
        sx={{
          height: "68vh",
          width: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <CustomDataGridV2
          showPagination={true}
          rows={filteredRows}
          page={page}
          rowsPerPage={rowsPerPage}
          totalItems={totalDeliveryMarketCount || filteredRows.length}
          handlePageChange={handlePageChange}
          columns={COLUMNS}
          isMultiLine={true}
        />
      </Box>

      <AddDeliveryMarket
        open={openAddDialog}
        setOpenAddDialog={setOpenAddDialog}
        setPage={setPage}
        setTableRowsData={setTableRowsData}
        setTotalDeliveryMarketCount={setTotalDeliveryMarketCount}
        rowsPerPage={rowsPerPage}
      />

      <EditDeliveryMarket
        open={openEditDialog}
        setOpenEditDialog={setOpenEditDialog}
        selectedRow={selectedRow}
        setTableRowsData={setTableRowsData}
        setTotalDeliveryMarketCount={setTotalDeliveryMarketCount}
        rowsPerPage={rowsPerPage}
        page={page}
      />

      <DeleteDeliveryMarket
        open={openDeleteDialog}
        setOpenDeleteDialog={setOpenDeleteDialog}
        selectedRow={selectedRow}
        setPage={setPage}
        page={page}
        rowsPerPage={rowsPerPage}
        filteredRows={filteredRows}
        setTableRowsData={setTableRowsData}
        setTotalDeliveryMarketCount={setTotalDeliveryMarketCount}
      />
    </Box>
  );
}

export default DeliveryMarket;

const AddDeliveryMarket = ({
  open,
  setOpenAddDialog,
  setPage,
  setTableRowsData,
  setTotalDeliveryMarketCount,
  rowsPerPage,
}) => {
  const dispatch = useDispatch();
  const [name, setName] = useState("");

  const handleClose = () => {
    setOpenAddDialog(false);
    setName("");
  };

  const handleAdd = () => {
    const reqObj = {
      current_time: moment().format(),
      name: name,
    };

    const addAndRefreshData = async () => {
      await dispatch(
        HandleApiActions({
          ...adminSettingsAPI.add_dellivery_market_details,
          params: reqObj,
          show_toast: true,
        }),
      );

      setPage(1);
      const resp = await dispatch(
        HandleApiActions({
          ...adminSettingsAPI.get_dellivery_market_details,
          show_toast: false,
          params: {
            page_number: 1,
            page_size: rowsPerPage,
          },
        }),
      );

      const deliveryMarkets = getDeliveryMarketsFromResponse(resp);
      const mappedData = mapDeliveryMarkets({
        deliveryMarkets,
        page: 1,
        rowsPerPage,
        includeTimestamps: false,
      });

      setTableRowsData(mappedData || []);
      setTotalDeliveryMarketCount(
        getTotalCountFromResponse(resp, deliveryMarkets),
      );
    };

    addAndRefreshData();

    handleClose();
  };

  return (
    <CustomDialogBoxV2
      open={open}
      onClose={handleClose}
      title={"Add Delivery Market"}
      successButtonName={"Add"}
      rejectButtonName={"Close"}
      onClickSuccess={handleAdd}
      onDisabled={!(name?.length > 2 && name?.trim() !== "")}
      actionButtonStyle={"continuous"}
      onClickReject={handleClose}
    >
      <Stack spacing={2} sx={{ width: "350px" }}>
        <CustomFormTextFieldV2
          label="Market Name"
          value={name}
          setValue={(val) => {
            if (val.length <= 35) {
              setName(val);
            }
          }}
          required={true}
          maxLength={35}
        />
      </Stack>
    </CustomDialogBoxV2>
  );
};

const EditDeliveryMarket = ({
  open,
  setOpenEditDialog,
  selectedRow,
  setTableRowsData,
  setTotalDeliveryMarketCount,
  rowsPerPage,
  page,
}) => {
  const [editName, setEditName] = useState("");

  useEffect(() => {
    if (selectedRow) {
      setEditName(selectedRow?.name || "");
    }
  }, [selectedRow]);

  const handleClose = () => {
    setOpenEditDialog(false);
  };

  return (
    <CustomDialogBoxV2
      open={open}
      onClose={handleClose}
      title={"UPDATE DELIVERY MARKET"}
      successButtonName={"Update"}
      rejectButtonName={"Close"}
      onClickSuccess={handleClose}
      actionButtonStyle={"continuous"}
      onClickReject={handleClose}
    >
      <Stack spacing={2}>
        <CustomFormTextFieldV2
          label="Market Name"
          value={editName}
          setValue={setEditName}
          txtFldStyle={{ width: "270px" }}
          required={true}
        />
      </Stack>
    </CustomDialogBoxV2>
  );
};

const DeleteDeliveryMarket = ({
  open,
  setOpenDeleteDialog,
  selectedRow,
  setPage,
  page,
  rowsPerPage,
  filteredRows,
  setTableRowsData,
  setTotalDeliveryMarketCount,
}) => {
  const dispatch = useDispatch();

  const refreshCurrentPage = async () => {
    const resp = await dispatch(
      HandleApiActions({
        ...adminSettingsAPI.get_dellivery_market_details,
        show_toast: false,
        params: {
          page_number: page,
          page_size: rowsPerPage,
        },
      }),
    );

    const deliveryMarkets = getDeliveryMarketsFromResponse(resp);
    const mappedData = mapDeliveryMarkets({
      deliveryMarkets,
      page,
      rowsPerPage,
      includeTimestamps: false,
    });

    setTableRowsData(mappedData || []);
    const totalCount = getTotalCountFromResponse(resp, deliveryMarkets);
    setTotalDeliveryMarketCount(totalCount);

    if (Array.isArray(mappedData) && mappedData.length === 0 && page > 1) {
      setPage(page - 1);
    }
  };

  const handleDelete = () => {
    const reqObj = {
      current_time: moment().format(),
      name: selectedRow?.allData?.name,
    };

    dispatch(
      HandleApiActions({
        ...adminSettingsAPI.delete_deliver_market_detials,
        params: reqObj,
        show_toast: true,
      }),
    ).then(() => {
      setTimeout(() => {
        refreshCurrentPage();
      }, 500);
    });

    setOpenDeleteDialog(false);
  };

  return (
    <CustomDialogBoxV2
      open={open}
      onClose={() => setOpenDeleteDialog(false)}
      title={"Delete Delivery Market"}
      successButtonName={"Ok"}
      rejectButtonName={"Cancel"}
      onClickSuccess={handleDelete}
      onClickReject={() => setOpenDeleteDialog(false)}
      actionButtonStyle={"continuous"}
    >
      <Box
        sx={{
          width: "350px",
          height: "50px",
          display: "flex",
          alignItems: "center",
        }}
      >
        <DialogContentText>Are you sure you want to delete?</DialogContentText>
      </Box>
    </CustomDialogBoxV2>
  );
};

const selectedRowShape = PropTypes.shape({
  name: PropTypes.string,
  allData: PropTypes.shape({
    name: PropTypes.string,
  }),
});

DeliveryMarket.propTypes = {
  onExportDataReady: PropTypes.func,
  handleExportToExcel: PropTypes.func,
};

AddDeliveryMarket.propTypes = {
  open: PropTypes.bool,
  setOpenAddDialog: PropTypes.func,
  setPage: PropTypes.func,
  setTableRowsData: PropTypes.func,
  setTotalDeliveryMarketCount: PropTypes.func,
  rowsPerPage: PropTypes.number,
};

EditDeliveryMarket.propTypes = {
  open: PropTypes.bool,
  setOpenEditDialog: PropTypes.func,
  selectedRow: selectedRowShape,
  setTableRowsData: PropTypes.func,
  setTotalDeliveryMarketCount: PropTypes.func,
  rowsPerPage: PropTypes.number,
  page: PropTypes.number,
};

DeleteDeliveryMarket.propTypes = {
  open: PropTypes.bool,
  setOpenDeleteDialog: PropTypes.func,
  selectedRow: selectedRowShape,
  setPage: PropTypes.func,
  page: PropTypes.number,
  rowsPerPage: PropTypes.number,
  filteredRows: PropTypes.array,
  setTableRowsData: PropTypes.func,
  setTotalDeliveryMarketCount: PropTypes.func,
};
